import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setLanguage } from "../i18n/i18n.js";
import { resources } from "../i18n/resources.js";
import {
  Bell, Search, User, Settings, LogOut, Edit, Building,
  ShieldCheck, Users, MessageSquare, AlertCircle, X, Info,
  DollarSign, Globe, HeartHandshake, Coins, ShoppingBag, Crown, Menu,
  Compass,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { useSocket } from "./Context.jsx";
import SearchModal from "./SearchModal";
import NotificationModal from "./NotificationModal";
import Registratioon from "./registratioon";
import { getGroupUnreadCount } from "../utils/messageBadge.js";
import LevelBadge from "./LevelBadge.jsx";
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { MARKET } from '../utils/market.js';
import "./Navbar.css";

// Only fully-translated languages are offered — de/es/pt in resources.js are
// still unfinished (under 10% translated) and would otherwise show a broken
// mix of that language and the fallback for most of the site. fr is now
// fully translated (see resources.js) and offered alongside hy/en/ru.
const LANGUAGE_CODES = ["hy", "en", "ru", "fr"].filter((code) => code in resources);

function Navbar() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [bellPulsing, setBellPulsing] = useState(false);
  const { on: onSocketEvent, off: offSocketEvent } = useSocket();
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  // currentUser.level only ever reflects whatever it was at the last login/full page load —
  // nothing re-fetches it as XP is earned during a live session (posting, applying, daily
  // login bonus, etc.), so the badge would silently go stale for the whole session otherwise.
  // Refetching the small /gamification/me summary each time the dropdown opens keeps it honest
  // without adding a poll loop for something this low-stakes.
  const [liveLevel, setLiveLevel] = useState(null);
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ users: [], posts: [], communities: [], organizations: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const notificationModalRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const settingsRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const langMenuRef = useRef(null);
  const langButtonRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(user);
  const [avatarErrors, setAvatarErrors] = useState({});
  const defaultAvatar = "/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png";

  // ── Close all panels, keep only `except` open ──
  const closeAll = (except = null) => {
    if (except !== "search")       { setIsSearchOpen(false); setSearchQuery(""); }
    if (except !== "notification") setIsNotificationModalOpen(false);
    if (except !== "settings")     setSettingsOpen(false);
    if (except !== "userDropdown") setUserDropdownOpen(false);
    if (except !== "lang")         setLangMenuOpen(false);
  };

  useEffect(() => {
    const screenH = window.screen.height;
    const onVR = () => { const v = window.visualViewport?.height || window.innerHeight; setIsInputFocused(v < screenH - 100); };
    const onFocus = (e) => { if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") setIsInputFocused(true); };
    const onBlur = (e) => { if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") { setTimeout(() => { const v = window.visualViewport?.height || window.innerHeight; if (v >= screenH - 100) setIsInputFocused(false); }, 100); } };
    if (window.visualViewport) window.visualViewport.addEventListener("resize", onVR);
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("focusout", onBlur, true);
    return () => { if (window.visualViewport) window.visualViewport.removeEventListener("resize", onVR); document.removeEventListener("focusin", onFocus, true); document.removeEventListener("focusout", onBlur, true); };
  }, []);

  useEffect(() => { if (isUserModalOpen) closeAll(); }, [isUserModalOpen]);

  useEffect(() => {
    const fn = (e) => {
      const tgt = e.target;
      if (!desktopSearchRef.current?.contains(tgt) && !mobileSearchRef.current?.contains(tgt) && isSearchOpen) { setIsSearchOpen(false); setSearchQuery(""); }
      if (!settingsRef.current?.contains(tgt) && !settingsButtonRef.current?.contains(tgt) && settingsOpen) setSettingsOpen(false);
      if (!langMenuRef.current?.contains(tgt) && !langButtonRef.current?.contains(tgt) && langMenuOpen) setLangMenuOpen(false);
      if (!dropdownRef.current?.contains(tgt) && userDropdownOpen) setUserDropdownOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [isSearchOpen, settingsOpen, userDropdownOpen, langMenuOpen]);

  useEffect(() => { setIsSearchOpen(false); setSearchQuery(""); setMenuOpen(false); setMobileSearchOpen(false); }, [location.pathname]);
  useEffect(() => { setCurrentUser(user); }, [user]);
  useEffect(() => { const fn = (e) => { if (e.detail?.user) { setCurrentUser(e.detail.user); setImageRefreshKey(Date.now()); } }; window.addEventListener("userProfileUpdated", fn); return () => window.removeEventListener("userProfileUpdated", fn); }, []);
  useEffect(() => { const iv = setInterval(() => { if (document.hidden) return; try { const s = localStorage.getItem("user"); if (s && currentUser) { const p = JSON.parse(s); if (p.profileImage !== currentUser.profileImage) { setCurrentUser(p); setImageRefreshKey(Date.now()); } } } catch {} }, 3000); return () => clearInterval(iv); }, [currentUser]);
  useEffect(() => {
    // Backgrounded tabs used to keep hitting this endpoint every 10s forever — pausing
    // while hidden and refreshing once on return cuts idle-tab server load with no
    // visible staleness (the socket "new-notification" listener below still bumps the
    // count live while the tab is in the foreground).
    const load = async () => { const token = localStorage.getItem("token"); if (!token || !currentUser) return; try { const r = await fetch(`${GREENWICH_BACKEND_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const d = await r.json(); setNotificationCount(d.count || 0); } } catch { setNotificationCount(0); } };
    if (currentUser) {
      load();
      const iv = setInterval(() => { if (!document.hidden) load(); }, 10000);
      const onVisible = () => { if (!document.hidden) load(); };
      document.addEventListener("visibilitychange", onVisible);
      return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
    }
  }, [currentUser]);
  useEffect(() => {
    if (!userDropdownOpen || !currentUser) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${GREENWICH_BACKEND_URL}/api/gamification/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d.success) setLiveLevel(d.level); })
      .catch(() => {});
  }, [userDropdownOpen, currentUser]);
  useEffect(() => {
    if (!currentUser) return;
    const handleNew = () => {
      setNotificationCount((c) => c + 1);
      setBellPulsing(true);
      setTimeout(() => setBellPulsing(false), 900);
    };
    const cleanup = onSocketEvent("new-notification", handleNew);
    return () => { offSocketEvent("new-notification", handleNew); cleanup?.(); };
  }, [currentUser, onSocketEvent, offSocketEvent]);
  useEffect(() => {
    const loadMessageUnreadCount = async () => {
      const token = localStorage.getItem("token");
      if (!token || !currentUser?._id) {
        setMessageUnreadCount(0);
        return;
      }

      try {
        const [conversationsRes, groupsRes] = await Promise.all([
          fetch(`${GREENWICH_BACKEND_URL}/api/conversations`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${GREENWICH_BACKEND_URL}/api/community/groups`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        const conversationsData = conversationsRes.ok ? await conversationsRes.json() : { conversations: [] };
        const groupsData = groupsRes.ok ? await groupsRes.json() : { groups: [] };
        const directUnread = (conversationsData.conversations || []).reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
        const memberGroups = (groupsData.groups || []).filter(group => {
          const isMember = group.members?.some(member => (typeof member === "object" ? member._id : member) === currentUser._id);
          const isCreator = group.createdBy === currentUser._id || group.createdBy?._id === currentUser._id;
          return isMember || isCreator || group.isMember;
        });
        const groupUnread = getGroupUnreadCount(memberGroups, currentUser._id);
        setMessageUnreadCount(directUnread + groupUnread);
      } catch {
        setMessageUnreadCount(0);
      }
    };

    if (currentUser) {
      loadMessageUnreadCount();
      const intervalId = setInterval(() => { if (!document.hidden) loadMessageUnreadCount(); }, 10000);
      const onStorage = () => loadMessageUnreadCount();
      const onVisibility = () => { if (!document.hidden) loadMessageUnreadCount(); };
      window.addEventListener("storage", onStorage);
      window.addEventListener("focus", onStorage);
      window.addEventListener("groupMessageReadStateChanged", onStorage);
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        clearInterval(intervalId);
        window.removeEventListener("storage", onStorage);
        window.removeEventListener("focus", onStorage);
        window.removeEventListener("groupMessageReadStateChanged", onStorage);
        document.removeEventListener("visibilitychange", onVisibility);
      };
    }
  }, [currentUser]);
  useEffect(() => { document.body.style.overflow = menuOpen ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [menuOpen]);
  useEffect(() => { if (mobileSearchOpen) setTimeout(() => mobileSearchInputRef.current?.focus(), 50); }, [mobileSearchOpen]);
  useEffect(() => {
    if (!isSearchOpen) return undefined;

    const focusSearchInput = () => {
      const inputs = Array.from(document.querySelectorAll('[data-navbar-search-input="true"]'));
      const input = inputs.find((el) => {
        const style = window.getComputedStyle(el);
        return el.getClientRects().length > 0 && style.visibility !== "hidden" && style.display !== "none";
      }) || searchInputRef.current;

      if (!input) return;
      input.focus({ preventScroll: true });
      input.select?.();
    };

    focusSearchInput();
    const frameId = requestAnimationFrame(focusSearchInput);
    const timerIds = [50, 150].map(delay => setTimeout(focusSearchInput, delay));

    return () => {
      cancelAnimationFrame(frameId);
      timerIds.forEach(clearTimeout);
    };
  }, [isSearchOpen]);

  const focusVisibleSearchInput = () => {
    setTimeout(() => {
      const inputs = Array.from(document.querySelectorAll('[data-navbar-search-input="true"]'));
      const input = inputs.find((el) => {
        const style = window.getComputedStyle(el);
        return el.getClientRects().length > 0 && style.visibility !== "hidden" && style.display !== "none";
      }) || searchInputRef.current;
      input?.focus?.({ preventScroll: true });
    }, 0);
  };

  const getUserAvatarUrl = (imageUrl, userId = null) => {
    if (!imageUrl || ["default","null","undefined","","no-avatar.png","default-avatar.png"].includes(imageUrl) || imageUrl.includes("default-avatar") || imageUrl.trim() === "") return defaultAvatar;
    if (userId && avatarErrors[userId]) return defaultAvatar;
    if (imageUrl.startsWith("blob:") || imageUrl.startsWith("data:image") || imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) return imageUrl;
    if (imageUrl.includes("uploads/") || imageUrl.includes("profiles/")) { const idx = imageUrl.indexOf("uploads/"); if (idx !== -1) return `${GREENWICH_BACKEND_URL}/${imageUrl.substring(idx)}`; return `${GREENWICH_BACKEND_URL}${imageUrl.startsWith("/") ? imageUrl : "/" + imageUrl}`; }
    return defaultAvatar;
  };
  const getUserDisplayName = (u) => {
    if (u.userType === "organizer" || u.accountType === "organization") { if (u.username) return u.username; if (u.organizationName) return u.organizationName; }
    if (u.organizationName && u.accountType === "organization") return u.organizationName;
    if (u.name || u.surname) return `${u.name || ""} ${u.surname || ""}`.trim();
    if (u.username) return u.username; if (u.email) return u.email.split("@")[0]; return "Unknown User";
  };
  const getUserImage = (u) => {
    const sources = [u.profileImage, u.avatar, u.image, u.profilePicture, u.logo, u.profileImageUrl, u.avatarUrl];
    for (const src of sources) { if (src && !["default","null","undefined","","no-avatar.png"].includes(src)) return getUserAvatarUrl(src, u._id || u.id); }
    return defaultAvatar;
  };

  const handleResultClick = (item, type) => {
    setIsSearchOpen(false); setMobileSearchOpen(false); setSearchQuery(""); setSearchResults({ users: [], posts: [], communities: [], organizations: [] });
    const id = item.id || item._id; if (!id) return;
    setTimeout(() => { switch (type) { case "user": navigate(`/profile/${id}`); break; case "organization": navigate(`/organization/${id}`); break; case "post": navigate(`/post/${id}`); break; case "community": navigate(`/community/group/${id}`); break; default: navigate(`/profile/${id}`); } }, 50);
  };
  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    const input = e.target;
    setSearchQuery(value);
    setSearchError(null);
    setTimeout(() => input?.focus?.(), 0);
    clearTimeout(searchDebounceRef.current);

    if (value.trim().length < 2) {
      searchRequestIdRef.current += 1;
      setSearchLoading(false);
      setSearchResults({ users: [], posts: [], communities: [], organizations: [] });
      return;
    }

    searchDebounceRef.current = setTimeout(() => performSearch(value), 300);
  };

  const performSearch = async (query) => {
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    if (!query.trim()) return; setSearchLoading(true); setSearchError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) { if (requestId === searchRequestIdRef.current) setSearchError(t("navbar.loginToSearch")); setSearchLoading(false); return; }
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/search?q=${encodeURIComponent(query)}&type=all`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      if (requestId !== searchRequestIdRef.current) return;
      if (response.status === 401) { localStorage.removeItem("token"); localStorage.removeItem("user"); setSearchError(t("navbar.sessionExpired")); setSearchLoading(false); return; }
      if (response.ok) {
        const data = await response.json();
        if (requestId !== searchRequestIdRef.current) return;
        // data.users already contains every matched user, including everyone already in
        // data.organizers/data.players — concatenating all three duplicated each person.
        const seenUserIds = new Set();
        const allUsers = [...(data.organizers || []).map(org => ({ ...org, accountType: "organization", profileImage: org.profileImage || org.logo || defaultAvatar })), ...(data.players || []).map(p => ({ ...p, accountType: "player", profileImage: p.profileImage || defaultAvatar })), ...(data.users || []).map(u => ({ ...u, accountType: "user", profileImage: u.profileImage || defaultAvatar }))].filter(u => {
          if (!u._id || seenUserIds.has(u._id)) return false;
          seenUserIds.add(u._id);
          return true;
        });
        setSearchResults({ users: allUsers, posts: data.posts || [], communities: (data.communities || []).map(c => ({ ...c, image: c.image || c.profileImage || defaultAvatar })), organizations: data.organizers || [] });
      } else { const err = await response.json().catch(() => ({ error: t("navbar.searchFailed") })); setSearchError(err.error || t("navbar.searchFailed")); setSearchResults({ users: [], posts: [], communities: [], organizations: [] }); }
    } catch { setSearchError(t("navbar.networkError")); setSearchResults({ users: [], posts: [], communities: [], organizations: [] }); }
    finally { if (requestId === searchRequestIdRef.current) setSearchLoading(false); }
  };

  const isNavActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const handleUserClick = (e) => { e.stopPropagation(); if (currentUser) { const next = !userDropdownOpen; closeAll("userDropdown"); setUserDropdownOpen(next); } else { closeAll(); navigate("/registration"); } };
  const handleSettingsClick = (e) => { e.stopPropagation(); const next = !settingsOpen; closeAll("settings"); setSettingsOpen(next); };
  const toggleNotificationModal = (e) => { if (e) e.stopPropagation(); const next = !isNotificationModalOpen; closeAll("notification"); setIsNotificationModalOpen(next); };
  const toggleSearch = (e) => { if (e) e.stopPropagation(); const next = !isSearchOpen; closeAll("search"); setIsSearchOpen(next); if (next) focusVisibleSearchInput(); };
  const toggleLang = (e) => { e.stopPropagation(); const next = !langMenuOpen; closeAll("lang"); setLangMenuOpen(next); };

  // Manual AM/FR switch, available whether or not the visitor is logged in —
  // saves the choice so public/index.html's auto-redirect never overrides it
  // again, then sends the browser to the matching domain.
  const switchMarket = (next) => {
    try { localStorage.setItem('greenwich_market_pref', next); } catch (e) {}
    const targetHost = next === 'FR' ? 'fr.greenwich.am' : 'greenwich.am';
    if (window.location.hostname.toLowerCase() !== targetHost) {
      // localStorage doesn't carry over to the other domain — pass the choice
      // as ?market=.. too, so public/index.html's inline script can save it
      // to that origin's own localStorage before its auto-detect can run.
      const qs = new URLSearchParams(window.location.search);
      qs.set('market', next);
      window.location.href = `https://${targetHost}${window.location.pathname}?${qs.toString()}`;
    }
  };

  const handleSettingsItemClick = () => { setSettingsOpen(false); setMenuOpen(false); navigate("/settings"); };
  // The guided tour (mascot walkthrough) otherwise only ever auto-plays once, right after a
  // brand-new player finishes registration — anyone who skipped it, missed it, or already had
  // an account before the feature shipped has no other way to see it. App.js listens for this
  // event, jumps to /feed (the only page the tour points at) and starts it on demand.
  const handleHowItWorksClick = () => { setUserDropdownOpen(false); setMenuOpen(false); window.dispatchEvent(new CustomEvent("greenwich:start-onboarding")); };
  const handleWhoWeAreClick    = () => { setSettingsOpen(false); setMenuOpen(false); navigate("/whoweare"); };
  const handleContactUsClick   = () => { setSettingsOpen(false); setMenuOpen(false); navigate("/contact"); };
  const handleGreencoinsClick  = () => { setSettingsOpen(false); setMenuOpen(false); navigate(currentUser?.userType === "organizer" ? "/organization/greencoins" : "/greencoins"); };
  const handleShopClick        = () => { setSettingsOpen(false); setMenuOpen(false); navigate("/shop"); };
  const handleProClick         = () => { setSettingsOpen(false); setMenuOpen(false); navigate("/pro"); };
  const handleAccountClick     = () => { if (currentUser) navigate("/dashboard"); else navigate("/registration"); setUserDropdownOpen(false); setMenuOpen(false); };
  const handleEditProfileClick = () => { if (currentUser) navigate("/dashboard"); else navigate("/registration"); setUserDropdownOpen(false); setMenuOpen(false); };
  // The marketing landing page ("/home") is for a first-time web visitor deciding
  // whether to sign up — inside the app shell there's no such visitor, so the logo
  // goes to Programs (or Feed, if already logged in) instead. Native app detection
  // via the global Capacitor injects, same as App.js's RoleBasedFeed.
  const handleLogoClick        = () => {
    const isNative = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
    navigate(isNative ? (currentUser ? "/feed" : "/programs") : "/home");
    setMenuOpen(false); setUserDropdownOpen(false); setSettingsOpen(false);
  };
  const handleLogout           = () => { logout(); navigate("/"); setUserDropdownOpen(false); setMenuOpen(false); setSettingsOpen(false); };
  const handleOrganizationsClick = () => {
    setIsSearchOpen(false);
    if (!currentUser) { navigate("/registration"); setMenuOpen(false); return; }
    navigate("/verified-organizations", { state: { openOrgModal: true } });
    setMenuOpen(false); setUserDropdownOpen(false); setSettingsOpen(false);
  };
  const handleDonateClick = () => {
    if (!currentUser) { navigate("/registration"); setMenuOpen(false); return; }
    navigate("/donate"); setSettingsOpen(false); setUserDropdownOpen(false); setMenuOpen(false);
  };
  const handleBuddyClick = () => {
    navigate("/buddy"); setSettingsOpen(false); setUserDropdownOpen(false); setMenuOpen(false);
  };

  const bottomNavItems = [
    { key: "feed",      label: t("navbar.feed"),      path: "/feed",      authRequired: true,  img: "/nkarner/homee.png" },
    { key: "community", label: t("navbar.community"), path: "/community", authRequired: true,  img: "/nkarner/comm.PNG" },
    { key: "projects",  label: t("navbar.projects"),  path: "/programs",  authRequired: false, img: "/nkarner/hrtir.png" },
    { key: "messages",  label: t("navbar.messages"),  path: "/messages",  authRequired: true,  img: "/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png", rotate: true },
    { key: "account",   label: t("navbar.account"),   path: null,         authRequired: false, img: "/nkarner/acccount.PNG" },
  ];
  const handleBottomNav = (item) => {
    if (item.key === "account") { if (currentUser) navigate("/dashboard"); else navigate("/registration"); setMenuOpen(false); return; }
    if (item.authRequired && !currentUser) { navigate("/registration"); return; }
    navigate(item.path); setMobileSearchOpen(false);
  };
  const isBottomActive = (item) => {
    if (item.key === "account") return location.pathname.startsWith("/dashboard") || location.pathname.startsWith("/profile");
    if (!item.path) return false;
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const SearchPanel = ({ mobileRight }) => (
    <div
      onClick={e => e.stopPropagation()}
      className={`sp-panel${mobileRight ? " sp-panel--mobile-right" : ""}`}
    >
      <div className="sp-header">
        <div className="sp-input-wrap">
          <Search size={16} className="sp-input-icon" />
          <input ref={searchInputRef} data-navbar-search-input="true" type="text" value={searchQuery} onChange={handleSearchChange}
            placeholder={t("navbar.searchPlaceholder")} onClick={e => e.stopPropagation()} className="sp-input"
            autoFocus />
          {searchQuery && <button className="sp-clear-btn" onClick={e => { e.stopPropagation(); setSearchQuery(""); setSearchResults({ users: [], posts: [], communities: [], organizations: [] }); }}><X size={13} /></button>}
        </div>
      </div>
      <div className="sp-scroll">
        {searchLoading && <div className="sp-hint"><div className="sp-spinner" /><span className="sp-hint-text">{t("navbar.searching")}</span></div>}
        {searchError && !searchLoading && <div className="sp-error"><AlertCircle size={14} style={{ flexShrink: 0 }} />{searchError}</div>}
        {!searchLoading && !searchError && searchQuery.trim().length < 2 && <div className="sp-hint"><div className="sp-hint-icon"><Search size={22} /></div><span className="sp-hint-text">{t("navbar.searchMinChars")}</span></div>}
        {!searchLoading && !searchError && searchQuery.trim().length >= 2 && (
          <>
            {searchResults.users.length > 0 && (
              <div className="sp-section">
                <div className="sp-section-label"><User size={11} style={{ flexShrink: 0 }} />{t("navbar.users")}<span className="sp-badge">{searchResults.users.length}</span></div>
                {searchResults.users.slice(0, 5).map((u, i) => { const name = getUserDisplayName(u); const img = getUserImage(u); const isOrg = u.accountType === "organization"; return (
                  <button key={`u-${u._id||u.id||i}`} type="button" onClick={e => { e.stopPropagation(); handleResultClick(u, isOrg ? "organization" : "user"); }} className="sp-result-btn">
                    <img src={img} alt={name} className={`sp-avatar${isOrg ? " sp-avatar--org" : ""}`} onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="sp-name-row"><span>{name}</span>{isOrg && <span className="sp-org-badge">Org</span>}</div>
                      <div className="sp-sub">{isOrg ? t("navbar.organization") : t("navbar.user")}{u.username ? ` · @${u.username}` : ""}</div>
                    </div>
                  </button>); })}
              </div>)}
            {searchResults.communities.length > 0 && (
              <div className="sp-section">
                <div className="sp-section-label"><Users size={11} style={{ flexShrink: 0 }} />{t("navbar.communities")}<span className="sp-badge">{searchResults.communities.length}</span></div>
                {searchResults.communities.slice(0, 3).map((c, i) => (
                  <button key={`c-${c._id||c.id||i}`} type="button" onClick={e => { e.stopPropagation(); handleResultClick(c, "community"); }} className="sp-result-btn">
                    <img src={getUserAvatarUrl(c.image, c._id||c.id)} alt={c.name} className="sp-avatar sp-avatar--org" onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }} />
                    <div style={{ flex: 1, minWidth: 0 }}><div className="sp-name">{c.name||"Unnamed"}</div><div className="sp-sub">{c.members?.length||0} {t("common.members")}</div></div>
                  </button>))}
              </div>)}
            {searchResults.posts.length > 0 && (
              <div className="sp-section">
                <div className="sp-section-label"><MessageSquare size={11} style={{ flexShrink: 0 }} />{t("navbar.posts")}<span className="sp-badge">{searchResults.posts.length}</span></div>
                {searchResults.posts.slice(0, 3).map((p, i) => (
                  <button key={`p-${p._id||p.id||i}`} type="button" onClick={e => { e.stopPropagation(); handleResultClick(p, "post"); }} className="sp-result-btn sp-result-btn--post">
                    <div className="sp-post-icon"><MessageSquare size={18} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="sp-name">{t("navbar.postBy")} {p.author?.name||t("navbar.user")}</div>
                      <div className="sp-post-body">{p.content?.substring(0,80)}{p.content?.length>80?"...":""}</div>
                      {p.createdAt && <div className="sp-sub" style={{ marginTop: 4 }}>{new Date(p.createdAt).toLocaleDateString()}</div>}
                    </div>
                  </button>))}
              </div>)}
            {searchResults.users.length===0 && searchResults.posts.length===0 && searchResults.communities.length===0 && (
              <div className="sp-hint"><div className="sp-hint-icon"><Search size={22} /></div><p className="sp-hint-title">{t("navbar.noResults")} "{searchQuery}"</p><span className="sp-hint-text">{t("navbar.tryDifferentKeywords")}</span></div>)}
            {(searchResults.users.length>0||searchResults.posts.length>0||searchResults.communities.length>0) && (
              <div className="sp-see-all-wrap">
                <button className="sp-see-all-btn" onClick={e => { e.stopPropagation(); setIsSearchOpen(false); setMobileSearchOpen(false); setMenuOpen(false); navigate(`/search?q=${encodeURIComponent(searchQuery)}`); }}>
                  {t("navbar.seeAllResults")}
                </button>
              </div>)}
          </>)}
      </div>
    </div>
  );

  return (
    <div className="gw-navbar">
      {/* ═══════════════════ DESKTOP NAVBAR ═══════════════════ */}
      <nav className="navbar desktop-navbar">
        <div className="navbar-inner">

          <div className="logo" onClick={handleLogoClick} style={{ cursor: "pointer" }}>
            <img src="/nkarner/logo.png" alt="logo" />
            <span>Greenwich</span>
          </div>

          <ul className="nav-links">
            <li className={`nav-item clay-nav-link${isNavActive("/feed") ? " nav-item--active" : ""}`} onClick={() => { if (currentUser) navigate("/feed"); else navigate("/registration"); }}>
              <span className="nav-item__label">{t("navbar.feed")}</span><span className="nav-item__bar" />
            </li>
            <li className={`nav-item clay-nav-link${isNavActive("/community") ? " nav-item--active" : ""}`} onClick={() => { if (currentUser) navigate("/community"); else navigate("/registration"); }}>
              <span className="nav-item__label">{t("navbar.community")}</span><span className="nav-item__bar" />
            </li>
            <li className={`nav-item clay-nav-link${isNavActive("/programs") ? " nav-item--active" : ""}`} onClick={() => navigate("/programs")}>
              <span className="nav-item__label">{t("navbar.projects")}</span><span className="nav-item__bar" />
            </li>
            <li className={`nav-item clay-nav-link${isNavActive("/verified-organizations") ? " nav-item--active" : ""}`} onClick={handleOrganizationsClick}>
              <span className="nav-item__label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {t("navbar.organizations")}
                {currentUser?.role === "admin" && (
                  <span className="nav-item-admin-badge">
                    <ShieldCheck size={10} /> Admin
                  </span>
                )}
              </span>
              <span className="nav-item__bar" />
            </li>
            {currentUser?.role === "admin" && (
              <li className={`nav-item clay-nav-link nav-item--admin${isNavActive("/admin") ? " nav-item--active" : ""}`} onClick={() => navigate("/admin/dashboard")}>
                <span className="nav-item__label">{t("navbar.adminPanel")}</span><span className="nav-item__bar" />
              </li>
            )}
          </ul>

          <div className="nav-right">
            <button className="buddy-btn clay-press clay-focus-glow" onClick={handleBuddyClick}>
              <HeartHandshake size={16} /> {t("navbar.buddy")}
            </button>

            {/* ── All icons on one perfectly aligned row ── */}
            <div className="nav-icons-row">

              {/* Notification */}
              {currentUser && (
                <div ref={notificationRef} className="nav-icon-slot">
                  <button className={`nav-icon-btn notification-btn clay-press clay-focus-glow${bellPulsing ? " bell-pulsing" : ""}`} onClick={toggleNotificationModal}>
                    <Bell size={20} />
                    {notificationCount > 0 && <span className="notification-badge">{notificationCount > 99 ? "99+" : notificationCount}</span>}
                  </button>
                  <div ref={notificationModalRef}>
                    <NotificationModal isOpen={isNotificationModalOpen} onClose={() => setIsNotificationModalOpen(false)} />
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="nav-icon-slot navbar-search" ref={desktopSearchRef}>
                <button className="nav-icon-btn search-toggle-btn clay-press clay-focus-glow" type="button" onMouseDown={e => e.preventDefault()} onClick={toggleSearch}>
                  <Search size={20} />
                </button>
                {isSearchOpen && SearchPanel({})}
              </div>

              {/* User */}
              <div className="nav-icon-slot user-dropdown-wrapper" ref={dropdownRef}>
                <button className="user-btn clay-press clay-focus-glow" onClick={handleUserClick}>
                  {currentUser ? (
                    <>
                      <img src={getUserAvatarUrl(currentUser.profileImage, currentUser._id)} alt="avatar"
                        key={`nav-av-${imageRefreshKey}`}
                        className="user-avatar-img"
                        onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }} />
                      <span className="user-name-text">{getUserDisplayName(currentUser)}</span>
                    </>
                  ) : (
                    <>
                      <div className="user-avatar-placeholder">
                        <User size={18} />
                      </div>
                      <span className="user-name-text">{t("navbar.account")}</span>
                    </>
                  )}
                </button>
                {userDropdownOpen && currentUser && (
                  <div className="user-dropdown-menu clay-modal-enter" onClick={e => e.stopPropagation()}>
                    <div className="user-dropdown-header" onClick={handleAccountClick}>
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <img src={getUserAvatarUrl(currentUser.profileImage, currentUser._id)} alt="avatar" key={`dd-av-${imageRefreshKey}`}
                          className="user-dropdown-header-avatar"
                          onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }} />
                        <LevelBadge level={liveLevel ?? currentUser.level} size="sm" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="user-dropdown-header-name">{getUserDisplayName(currentUser)}</div>
                        <div className="user-dropdown-header-email">{currentUser.email}</div>
                        <div className="user-dropdown-header-cta">{currentUser.role === "admin" ? `${t("navbar.adminDashboard")} →` : `${t("navbar.viewDashboard")} →`}</div>
                      </div>
                    </div>
                    <button className="dropdown-item clay-press" onClick={handleEditProfileClick}><Edit size={18} />{t("navbar.editProfile")}</button>
                    <button className="dropdown-item clay-press" onClick={handleHowItWorksClick}><Compass size={18} />{t("navbar.howItWorks")}</button>
                    {currentUser.role === "admin" && (
                      <>
                        <div className="dropdown-admin-divider" />
                        <div className="dropdown-admin-label">
                          <ShieldCheck size={14} />Admin Tools
                        </div>
                        <button className="dropdown-item dropdown-item--admin clay-press" onClick={() => { navigate("/admin/verified-organizations"); setUserDropdownOpen(false); }}>
                          <Building size={18} />{t("navbar.verifiedOrganizations")}
                        </button>
                        <button className="dropdown-item dropdown-item--admin clay-press" onClick={() => { navigate("/admin/buddy-mentors"); setUserDropdownOpen(false); }}>
                          <HeartHandshake size={18} />{t("navbar.buddy")} mentors
                        </button>
                      </>
                    )}
                    <button className="dropdown-item dropdown-logout clay-press" onClick={handleLogout}><LogOut size={18} />{t("navbar.logOut")}</button>
                  </div>
                )}
              </div>

              {/* Language */}
              <div className="nav-icon-slot" ref={langMenuRef}>
                <button ref={langButtonRef} className="nav-icon-btn clay-press clay-focus-glow" type="button" onClick={toggleLang}>
                  <Globe size={20} />
                </button>
                {langMenuOpen && (
                  <div className="settings-dropdown-menu lang-dd clay-modal-enter" onClick={e => e.stopPropagation()}>
                    <div className="lang-dd-label">{t("navbar.selectLanguage")}</div>
                    {LANGUAGE_CODES.map(code => (
                      <button key={code} type="button" className={`lang-opt${i18n.language === code ? " lang-opt-active" : ""}`}
                        onClick={() => { setLanguage(code); i18n.changeLanguage(code); setLangMenuOpen(false); }}>
                        {t(`settings.languages.${code}`, { defaultValue: code.toUpperCase() })}
                      </button>
                    ))}
                    <div className="lang-dd-label" style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>{t("settings.privacy.market")}</div>
                    <button type="button" className={`lang-opt${MARKET === 'AM' ? " lang-opt-active" : ""}`} onClick={() => switchMarket('AM')}>
                      🇦🇲 Armenia
                    </button>
                    <button type="button" className={`lang-opt${MARKET === 'FR' ? " lang-opt-active" : ""}`} onClick={() => switchMarket('FR')}>
                      🇫🇷 France
                    </button>
                  </div>
                )}
              </div>

              {/* Settings */}
              <div className="nav-icon-slot settings-wrapper" ref={settingsRef}>
                <button ref={settingsButtonRef} className="nav-icon-btn settings-btn clay-press clay-focus-glow" onClick={handleSettingsClick}>
                  <Menu size={20} />
                </button>
                {settingsOpen && (
                  <div className="settings-dropdown-menu clay-modal-enter" onClick={e => e.stopPropagation()}>
                    <button className="dropdown-item clay-press" onClick={handleProClick}><Crown size={18} />{t("navbar.pro")}</button>
                    <button className="dropdown-item clay-press" onClick={handleDonateClick}><DollarSign size={18} />{t("navbar.donateNow")}</button>
                    <button className="dropdown-item clay-press" onClick={handleContactUsClick}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
                      {t("navbar.contactUs")}
                    </button>
                    <button className="dropdown-item clay-press" onClick={handleWhoWeAreClick}><Info size={18} />{t("navbar.whoWeAre")}</button>
                    <button className="dropdown-item clay-press" onClick={handleGreencoinsClick}><Coins size={18} />{t("navbar.greencoins")}</button>
                    <button className="dropdown-item clay-press" onClick={handleShopClick}><ShoppingBag size={18} />{t("navbar.shop")}</button>
                    <button className="dropdown-item clay-press" onClick={handleSettingsItemClick}><Settings size={18} />{t("navbar.settings")}</button>
                  </div>
                )}
              </div>

            </div>{/* end nav-icons-row */}
          </div>
        </div>
      </nav>

      {/* ═══════════════════ MOBILE TOP BAR ═══════════════════ */}
      <nav className="mobile-topbar">
        <div onClick={handleLogoClick} className="mobile-logo">
          <img src="/nkarner/logo.png" alt="logo" />
          <span>Greenwich</span>
        </div>
        <div className="mobile-icon-row">
          <div ref={notificationRef} style={{ position: "relative" }}>
            <button className={`mobile-icon-btn notification-btn${bellPulsing ? " bell-pulsing" : ""}`} onClick={toggleNotificationModal}>
              <Bell size={17} />
              {notificationCount > 0 && <span className="notification-badge">{notificationCount > 99 ? "99+" : notificationCount}</span>}
            </button>
            <div ref={notificationModalRef}>
              <NotificationModal isOpen={isNotificationModalOpen} onClose={() => setIsNotificationModalOpen(false)} />
            </div>
          </div>
          <div ref={mobileSearchRef} style={{ position: "relative" }}>
            <button className="mobile-icon-btn search-toggle-btn" type="button" onMouseDown={e => e.preventDefault()} onClick={toggleSearch}>
              <Search size={17} />
            </button>
            {isSearchOpen && SearchPanel({ mobileRight: true })}
          </div>
          <button className={`mobile-hamburger clay-press clay-focus-glow${menuOpen ? " active" : ""}`} onClick={() => setMenuOpen(p => !p)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* MOBILE DRAWER */}
      {menuOpen && <div className="drawer-backdrop" onClick={() => setMenuOpen(false)} />}
      <div className={`mobile-drawer${menuOpen ? " open clay-modal-enter" : ""}`}>
        <div className="drawer-header">
          {currentUser ? (
            <div className="drawer-user" onClick={() => { navigate("/dashboard"); setMenuOpen(false); }}>
              <img src={getUserAvatarUrl(currentUser.profileImage, currentUser._id)} alt="avatar"
                className="drawer-user-avatar"
                onError={e => { e.target.src = defaultAvatar; e.target.onerror = null; }} />
              <div style={{ minWidth: 0 }}>
                <div className="drawer-user-name">{getUserDisplayName(currentUser)}</div>
                <div className="drawer-user-sub">{t("navbar.viewDashboard")}</div>
              </div>
            </div>
          ) : (
            <button className="drawer-login-btn" onClick={() => { navigate("/registration"); setMenuOpen(false); }}>
              <User size={18} /> {t("navbar.account")}
            </button>
          )}
        </div>
        <div className="drawer-items">
          <button className="drawer-item clay-press" onClick={handleProClick}><div className="drawer-item-icon amber"><Crown size={18} /></div><span>{t("navbar.pro")}</span></button>
          <button className="drawer-item clay-press" onClick={handleBuddyClick}><div className="drawer-item-icon mint"><HeartHandshake size={18} /></div><span>{t("navbar.buddy")}</span></button>
          <button className="drawer-item clay-press" onClick={handleDonateClick}><div className="drawer-item-icon amber"><DollarSign size={18} /></div><span>{t("navbar.donateNow")}</span></button>
          <button className="drawer-item clay-press" onClick={handleOrganizationsClick}><div className="drawer-item-icon green"><Building size={18} /></div><span>{t("navbar.organizations")}</span></button>
          <button className="drawer-item clay-press" onClick={handleWhoWeAreClick}><div className="drawer-item-icon"><Info size={18} /></div><span>{t("navbar.whoWeAre")}</span></button>
          <button className="drawer-item clay-press" onClick={handleContactUsClick}><div className="drawer-item-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg></div><span>{t("navbar.contactUs")}</span></button>
          <div className="drawer-lang-label">{t("navbar.language")}</div>
          <div className="drawer-lang-row">
            {LANGUAGE_CODES.map(code => (
              <button key={code} type="button" className={`lang-chip${i18n.language === code ? " lang-chip-active" : ""}`}
                onClick={() => { setLanguage(code); i18n.changeLanguage(code); setMenuOpen(false); }}>
                {t(`settings.languages.${code}`, { defaultValue: code })}
              </button>
            ))}
          </div>
          <div className="drawer-lang-label">{t("settings.privacy.market")}</div>
          <div className="drawer-lang-row">
            <button type="button" className={`lang-chip${MARKET === 'AM' ? " lang-chip-active" : ""}`} onClick={() => switchMarket('AM')}>
              🇦🇲 Armenia
            </button>
            <button type="button" className={`lang-chip${MARKET === 'FR' ? " lang-chip-active" : ""}`} onClick={() => switchMarket('FR')}>
              🇫🇷 France
            </button>
          </div>
          <button className="drawer-item clay-press" onClick={handleGreencoinsClick}><div className="drawer-item-icon"><Coins size={18} /></div><span>{t("navbar.greencoins")}</span></button>
          <button className="drawer-item clay-press" onClick={handleShopClick}><div className="drawer-item-icon"><ShoppingBag size={18} /></div><span>{t("navbar.shop")}</span></button>
          <button className="drawer-item clay-press" onClick={handleSettingsItemClick}><div className="drawer-item-icon"><Settings size={18} /></div><span>{t("navbar.settings")}</span></button>
          {currentUser?.role === "admin" && (
            <button className="drawer-item" onClick={() => { navigate("/admin/dashboard"); setMenuOpen(false); }}>
              <div className="drawer-item-icon admin"><ShieldCheck size={18} /></div>
              <span className="drawer-item-admin-label">{t("navbar.adminPanel")}</span>
            </button>
          )}
        </div>
        {currentUser && <button className="drawer-logout clay-press" onClick={handleLogout}><LogOut size={17} /> {t("navbar.logOut")}</button>}
      </div>

      {/* MOBILE SEARCH OVERLAY */}
      {mobileSearchOpen && (
        <div className="mobile-search-overlay clay-fade-in">
          <div className="mobile-search-bar">
            <Search size={17} />
            <input ref={mobileSearchInputRef} type="text" value={searchQuery} onChange={handleSearchChange} placeholder={t("navbar.searchPlaceholder")}
              className="mobile-search-input" />
            {searchQuery && <button onClick={() => { setSearchQuery(""); setSearchResults({ users: [], posts: [], communities: [], organizations: [] }); }} className="mobile-search-clear"><X size={17} /></button>}
            <button onClick={() => { setMobileSearchOpen(false); setSearchQuery(""); }} className="mobile-search-cancel">{t("navbar.cancel")}</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {SearchPanel({})}
          </div>
        </div>
      )}

      {/* MOBILE BOTTOM NAV */}
      {!location.pathname.startsWith("/messages") && !location.pathname.startsWith("/group-chat") && !location.pathname.startsWith("/chat") && (
        <nav className={`mobile-bottom-nav${isInputFocused ? " mobile-bottom-nav--hidden" : ""}`}>
          <div className="bnav-pill">
            {bottomNavItems.map(item => {
              const active = isBottomActive(item);
              const imgSize = item.key === "feed" || item.key === "community" ? 36 : item.key === "messages" ? 24 : 30;
              return (
                <button key={item.key} className={`bnav-item clay-press${active ? " active" : ""}`} onClick={() => handleBottomNav(item)}>
                  <div className={`bnav-icon-wrap${active ? " active" : ""}`}>
                    <img src={item.img} alt={item.label} style={{ width: imgSize, height: imgSize, objectFit: "contain", transform: item.rotate ? "rotate(30deg)" : "none", display: "block" }} />
                    {item.key === "messages" && messageUnreadCount > 0 && (
                      <span className="notification-badge" style={{ top: -4, right: -6 }}>
                        {messageUnreadCount > 99 ? "99+" : messageUnreadCount}
                      </span>
                    )}
                  </div>
                  {active && <span className="bnav-active-dot" />}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {isSearchModalOpen && <SearchModal onClose={() => setIsSearchModalOpen(false)} />}
      <Registratioon isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} />
    </div>
  );
}

export default Navbar;
